/**
 * QuotationForm — form for creating a new quotation.
 *
 * Fields: client name, contact email, contact phone, total, discount, notes, valid until.
 * Uses react-hook-form for validation and submission.
 * Labels in Spanish.
 */

import { useForm } from 'react-hook-form'
import type { CreateQuotationDTO } from '@1klick/types'

interface QuotationFormProps {
  onSubmit: (data: CreateQuotationDTO) => void
  isSubmitting: boolean
}

export function QuotationForm({ onSubmit, isSubmitting }: QuotationFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateQuotationDTO>({
    defaultValues: {
      clientName: '',
      contactEmail: '',
      contactPhone: '',
      totalCents: 0,
      discountCents: 0,
      notes: '',
      validUntil: '',
    },
  })

  const onFormSubmit = (data: CreateQuotationDTO) => {
    // Clean optional empty strings to undefined
    const cleaned: CreateQuotationDTO = {
      ...data,
      contactPhone: data.contactPhone?.trim() || undefined,
      notes: data.notes?.trim() || undefined,
      validUntil: data.validUntil?.trim() || undefined,
      discountCents: data.discountCents || 0,
    }
    onSubmit(cleaned)
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* Client Name */}
        <div>
          <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">
            Nombre del cliente *
          </label>
          <input
            id="clientName"
            type="text"
            {...register('clientName', {
              required: 'El nombre del cliente es requerido',
              minLength: { value: 1, message: 'Minimo 1 caracter' },
              maxLength: { value: 255, message: 'Maximo 255 caracteres' },
            })}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Nombre de la empresa o persona"
          />
          {errors.clientName && (
            <p className="mt-1 text-sm text-red-600">{errors.clientName.message}</p>
          )}
        </div>

        {/* Contact Email */}
        <div>
          <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
            Email de contacto *
          </label>
          <input
            id="contactEmail"
            type="email"
            {...register('contactEmail', {
              required: 'El email de contacto es requerido',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Email invalido',
              },
            })}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="contacto@empresa.com"
          />
          {errors.contactEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.contactEmail.message}</p>
          )}
        </div>

        {/* Contact Phone */}
        <div>
          <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
            Telefono de contacto
          </label>
          <input
            id="contactPhone"
            type="tel"
            {...register('contactPhone', {
              maxLength: { value: 50, message: 'Maximo 50 caracteres' },
            })}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="+57 300 123 4567"
          />
          {errors.contactPhone && (
            <p className="mt-1 text-sm text-red-600">{errors.contactPhone.message}</p>
          )}
        </div>

        {/* Valid Until */}
        <div>
          <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700">
            Valida hasta
          </label>
          <input
            id="validUntil"
            type="date"
            {...register('validUntil')}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Total (cents) */}
        <div>
          <label htmlFor="totalCents" className="block text-sm font-medium text-gray-700">
            Total (centavos) *
          </label>
          <input
            id="totalCents"
            type="number"
            min="0"
            {...register('totalCents', {
              required: 'El total es requerido',
              valueAsNumber: true,
              min: { value: 0, message: 'El total debe ser mayor o igual a 0' },
            })}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="100000"
          />
          {errors.totalCents && (
            <p className="mt-1 text-sm text-red-600">{errors.totalCents.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">Ingrese el valor en centavos (ej: 100000 = $1,000.00)</p>
        </div>

        {/* Discount (cents) */}
        <div>
          <label htmlFor="discountCents" className="block text-sm font-medium text-gray-700">
            Descuento (centavos)
          </label>
          <input
            id="discountCents"
            type="number"
            min="0"
            {...register('discountCents', {
              valueAsNumber: true,
              min: { value: 0, message: 'El descuento debe ser mayor o igual a 0' },
            })}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="0"
          />
          {errors.discountCents && (
            <p className="mt-1 text-sm text-red-600">{errors.discountCents.message}</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
          Notas
        </label>
        <textarea
          id="notes"
          rows={3}
          {...register('notes', {
            maxLength: { value: 2000, message: 'Maximo 2000 caracteres' },
          })}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="Notas adicionales sobre la cotizacion..."
        />
        {errors.notes && (
          <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
        )}
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Creando...' : 'Crear cotizacion'}
        </button>
      </div>
    </form>
  )
}
